/**
 * StaffTrayPage — Mobile-first PWA opened from staff QR on physical tray.
 * Route: /s/:trayId
 *
 * PIN-gated. On unlock, jumps staff into a tray-scoped restock flow.
 */
import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, Lock, Package, ArrowLeft, Scale, CheckCircle2, AlertTriangle, ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export default function StaffTrayPage() {
  const { trayId } = useParams();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [unlocked, setUnlocked] = useState(null); // unlocked payload
  const [loading, setLoading] = useState(false);
  const [newWeights, setNewWeights] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [session, setSession] = useState(null);

  const unlock = useCallback(async () => {
    if (pin.length < 4) {
      toast.error('Enter at least 4 digits');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/tray/staff/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tray_id: trayId, staff_pin: pin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Invalid PIN');
      setUnlocked(d);
      // Pre-fill weights with par expected
      const pre = {};
      (d.slots || []).forEach((s) => {
        pre[s.slot_id] = (s.expected_weight_g || 0) * (s.expected_units || 0);
      });
      setNewWeights(pre);
      toast.success(`Unlocked · ${d.slots.length} slots`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [trayId, pin]);

  const startAndComplete = useCallback(async () => {
    if (!unlocked) return;
    setSubmitting(true);
    setResult(null);
    try {
      // 1. Start session
      const startR = await fetch(`${BACKEND_URL}/api/iot/restock/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: unlocked.hotel.id,
          room_id: unlocked.room_id,
          tray_ids: [trayId],
          staff_pin: pin,
          staff_id: unlocked.staff_id,
        }),
      });
      const startD = await startR.json();
      if (!startR.ok) throw new Error(startD.detail || 'Could not start session');
      setSession(startD.session);

      // 2. Complete with new readings
      const readings = { [trayId]: {} };
      Object.entries(newWeights).forEach(([sid, w]) => {
        readings[trayId][sid] = Number(w) || 0;
      });

      const completeR = await fetch(`${BACKEND_URL}/api/iot/restock/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: startD.session.id,
          staff_pin: pin,
          new_readings: readings,
        }),
      });
      const completeD = await completeR.json();
      if (!completeR.ok) throw new Error(completeD.detail || 'Could not finalize');
      setResult(completeD);
      toast.success(`Restock complete · ${completeD.shrinkage_count} event(s)`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  }, [unlocked, trayId, pin, newWeights]);

  // ── Locked state: PIN entry ──────────────────────────────────────
  if (!unlocked) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-white flex flex-col items-center justify-center p-6" data-testid="staff-tray-pin">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 flex items-center justify-center mb-4">
          <Lock className="w-7 h-7 text-cyan-400" />
        </div>
        <h1 className="text-lg font-semibold mb-1">Staff unlock</h1>
        <p className="text-xs text-[#94a3b8] mb-6 text-center">Tray <span className="font-mono text-[#F7931A]">{trayId}</span></p>

        <div className="w-full max-w-xs space-y-3">
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={8}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && unlock()}
            placeholder="Enter staff PIN"
            className="w-full bg-[#0d1220] border border-[#1a2235] rounded-xl px-4 py-3 text-center text-xl font-mono tracking-[0.5em] focus:border-cyan-500 outline-none"
            data-testid="staff-pin-input"
          />
          <Button
            onClick={unlock}
            disabled={loading}
            className="w-full bg-cyan-500 hover:bg-cyan-500/90 text-black"
            data-testid="staff-pin-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Unlock'}
          </Button>
        </div>

        <button
          onClick={() => navigate('/')}
          className="mt-6 text-xs text-[#64748b] hover:text-white flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> Cancel
        </button>
      </div>
    );
  }

  // ── Unlocked: tray slots + weight entry ──────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white pb-24" data-testid="staff-tray-unlocked">
      <header className="sticky top-0 z-10 bg-[#0a0e1a]/90 backdrop-blur border-b border-[#1a2235] px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center shrink-0">
            <Scale className="w-4 h-4 text-cyan-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-[#64748b]">Restock · {unlocked.hotel.name}</p>
            <h1 className="text-sm font-semibold truncate">Room {unlocked.room_number} · <span className="font-mono text-[#F7931A]">{trayId}</span></h1>
          </div>
          <Badge className="bg-cyan-500/10 text-cyan-400 text-[10px]">{unlocked.slots.length} slots</Badge>
        </div>
      </header>

      <section className="px-4 py-4 space-y-2">
        {unlocked.slots.map((s) => {
          const targetG = (s.expected_weight_g || 0) * (s.expected_units || 0);
          const curW = Number(newWeights[s.slot_id] || 0);
          const delta = curW - targetG;
          return (
            <div key={s.slot_id} className="bg-[#0d1220] border border-[#1a2235] rounded-2xl p-3" data-testid={`restock-slot-${s.slot_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-medium">Slot {s.slot_id} · {s.sku}</p>
                  <p className="text-[10px] text-[#64748b]">Target: {targetG.toFixed(0)} g · {s.expected_units} units · ${s.price_usd}</p>
                </div>
                {Math.abs(delta) < 15 ? (
                  <Badge className="bg-green-500/10 text-green-400 text-[10px]">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> OK
                  </Badge>
                ) : (
                  <Badge className="bg-amber-500/10 text-amber-400 text-[10px]">
                    <AlertTriangle className="w-3 h-3 mr-1" /> {delta > 0 ? `+${delta.toFixed(0)}` : delta.toFixed(0)} g
                  </Badge>
                )}
              </div>
              <input
                type="number"
                inputMode="decimal"
                value={newWeights[s.slot_id] ?? ''}
                onChange={(e) => setNewWeights((w) => ({ ...w, [s.slot_id]: e.target.value }))}
                className="w-full bg-[#080c16] border border-[#1a2235] rounded-lg px-3 py-2 text-sm font-mono focus:border-cyan-500 outline-none"
                placeholder={`Current weight (g) · target ${targetG.toFixed(0)}`}
                data-testid={`weight-input-${s.slot_id}`}
              />
            </div>
          );
        })}
      </section>

      {result && (
        <section className="px-4 mb-4" data-testid="restock-result">
          <div className="bg-[#0d1220] border border-[#1a2235] rounded-2xl p-4">
            <p className="text-sm font-semibold mb-2">Reconciliation complete</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-[#080c16] rounded p-2">
                <p className="text-[10px] text-[#64748b]">Events</p>
                <p className="text-lg font-mono">{result.shrinkage_count}</p>
              </div>
              <div className="bg-[#080c16] rounded p-2">
                <p className="text-[10px] text-[#64748b]">Loss (USD)</p>
                <p className="text-lg font-mono text-amber-400">${(result.total_loss_usd || 0).toFixed(2)}</p>
              </div>
            </div>
            {session && (
              <p className="text-[10px] text-[#64748b] mt-2">Session {session.id}</p>
            )}
          </div>
        </section>
      )}

      {/* Sticky submit */}
      <div className="fixed bottom-0 inset-x-0 bg-[#0a0e1a]/95 backdrop-blur border-t border-[#1a2235] p-3 z-20">
        <Button
          onClick={startAndComplete}
          disabled={submitting}
          className="w-full bg-cyan-500 hover:bg-cyan-500/90 text-black"
          data-testid="submit-restock-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
            <>Submit restock <ArrowRight className="w-4 h-4 ml-2" /></>
          )}
        </Button>
      </div>
    </div>
  );
}
